<template>
  <div class="charging-policies">
    <!-- Réservé aux gestionnaires du module (ONBOARDING_MODULE_MANAGE) : arrivé par une adresse directe, on le dit
         au lieu de charger des données refusées. -->
    <v-alert v-if="!canManage" type="info" outlined dense>{{ $t('onboarding.policy-page.manage-only') }}</v-alert>
    <template v-else>
    <charging-settings-tabs ref="tabs" @profile-saved="load" />
    <v-card class="mb-4">
      <v-card-text class="d-flex align-center flex-wrap" style="gap: 12px">
        <div class="flex-grow-1">
          <div class="text-h6">{{ $t('onboarding.charging-policy.list.title') }}</div>
          <div class="text-caption text--secondary">{{ $t('onboarding.charging-policy.list.explained') }}</div>
        </div>
        <v-switch v-model="withArchived" dense hide-details class="mt-0 mr-4" :label="$t('onboarding.charging-policy.list.show-archived')" @change="load" />
        <v-btn v-if="canManage" small color="primary" @click="openCreate()">
          <v-icon left small>mdi-plus</v-icon>{{ $t('onboarding.charging-policy.list.new') }}
        </v-btn>
        <audit-trail-button class="ml-2" entity-type="ChargingPolicy,OnboardingConfiguration,CommissioningPolicy" :organization-id="$route.params.id" />
      </v-card-text>
    </v-card>

    <v-alert v-if="error" type="error" dense outlined class="mb-4">{{ error }}</v-alert>
    <v-skeleton-loader v-if="loading" type="card, card" />

    <!-- Aucune politique (ou seulement des archivées, masquées) : dire quoi faire, pas une page blanche -->
    <v-card v-else-if="!policies.length" outlined class="mb-3">
      <v-card-text class="text-center py-8">
        <v-icon large color="grey lighten-1" class="mb-2">mdi-clipboard-text-outline</v-icon>
        <div class="text-body-1 mb-1">{{ $t('onboarding.charging-policy.list.empty-title') }}</div>
        <div class="text-caption text--secondary mb-4">{{ $t('onboarding.charging-policy.list.empty-text') }}</div>
        <v-btn v-if="canManage" small color="primary" @click="openCreate()">
          <v-icon left small>mdi-plus</v-icon>{{ $t('onboarding.charging-policy.list.new') }}
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- Une carte par politique : ce que reçoit la personne qu'on y invite, et son lien -->
    <v-card v-for="policy in policies" v-else :key="policy.id" class="mb-3 policy-card" :class="{ 'policy-card--archived': policy.archived }">
      <v-card-text>
        <div class="d-flex align-center flex-wrap" style="gap: 8px">
          <router-link :to="{ name: 'organizations.charging-policy', params: { policyId: policy.id } }" class="text-h6 policy-card__name">{{ policy.name }}</router-link>
          <v-chip v-if="policy.default" x-small color="primary" outlined>{{ $t('onboarding.charging-policy.list.default') }}</v-chip>
          <v-chip v-if="policy.archived" x-small>{{ $t('onboarding.charging-policy.list.archived') }}</v-chip>
          <v-chip v-if="!policy.published" x-small color="orange" outlined>{{ $t('onboarding.charging-policy.list.unpublished') }}</v-chip>
          <v-spacer />
          <v-chip x-small :color="linkColor(policy)" outlined>
            <v-icon x-small left>mdi-link-variant</v-icon>{{ linkLabel(policy) }}
          </v-chip>
          <v-menu v-if="canManage" bottom left offset-y>
            <template #activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
            </template>
            <v-list dense>
              <v-list-item :to="{ name: 'organizations.charging-policy', params: { policyId: policy.id } }">
                <v-list-item-title>{{ $t('onboarding.charging-policy.list.open') }}</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="!policy.default && !policy.archived" @click="update(policy, { default: true }, 'onboarding.charging-policy.list.default-ok')">
                <v-list-item-title>{{ $t('onboarding.charging-policy.list.make-default') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="openCreate(policy)">
                <v-list-item-title>{{ $t('onboarding.charging-policy.list.duplicate') }}</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="!policy.default && !policy.archived" @click="archive(policy)">
                <v-list-item-title class="error--text">{{ $t('onboarding.charging-policy.list.archive') }}</v-list-item-title>
              </v-list-item>
              <!-- Seulement une politique qui n'a jamais servi, celle d'origine comprise (le serveur le vérifie et, sinon, dit de l'archiver) -->
              <v-list-item v-if="!policy.default" @click="remove(policy)">
                <v-list-item-title class="error--text">{{ $t('onboarding.charging-policy.list.delete') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>

        <!-- Ses tiroirs, en une ligne chacun : ce que reçoit la personne qu'on y invite -->
        <p v-if="!policy.published" class="text-caption orange--text text--darken-3 mt-2 mb-0">{{ $t('onboarding.charging-policy.never-published') }}</p>
        <v-row v-else dense class="mt-2">
          <v-col v-for="box in boxesOf(policy)" :key="box.key" cols="12" sm="6" md="4">
            <div class="policy-card__box">
              <div class="policy-card__box-title"><v-icon x-small class="mr-1">{{ box.icon }}</v-icon>{{ box.title }}</div>
              <div class="text-body-2">{{ box.text }}</div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Nouvelle politique : vierge, ou partie d'une autre -->
    <v-dialog v-model="creation.open" max-width="520">
      <v-card>
        <v-card-title>{{ $t('onboarding.charging-policy.list.new') }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="creation.name" :label="$t('onboarding.charging-policy.list.name')" outlined dense autofocus />
          <v-select
              v-model="creation.copyFrom"
              :items="copyOptions"
              :label="$t('onboarding.charging-policy.list.start-from')"
              outlined dense hide-details
          />
          <v-alert v-if="creation.error" type="error" dense outlined class="mt-3 mb-0">{{ creation.error }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="creation.open = false">{{ $t('cancel') }}</v-btn>
          <v-btn small color="primary" :loading="creation.running" :disabled="!creation.name.trim()" @click="create">{{ $t('onboarding.charging-policy.list.create') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    </template>
  </div>
</template>

<script>
import AuditTrailButton from "@elements/AuditTrailButton.vue";
import ChargingPolicyRepository from '@repository/ChargingPolicyRepository'
import CommissioningPolicyRepository from '@repository/CommissioningPolicyRepository'
import EquipmentProfileRepository from '@repository/EquipmentProfileRepository'
import MemberTypeRepository from '@repository/MemberTypeRepository'
import ChargingSettingsTabs from './ChargingSettingsTabs.vue'
import CommissioningMixin from './CommissioningMixin'
import { KINDS_KEY, PROFILES_BY_DEVICE_TYPE_KEY, PROFILES_KEY, policyCatalogue } from './Commissioning/policyCatalogue'

const HOME_KINDS = ['charging-point', 'meter-device', 'vehicle-meter']
const CONNECTED_VEHICLE = 'connected-vehicle'

/**
 * Les politiques de recharge d'une organisation : un profil d'usage chacune (« salarié remboursé »,
 * « salarié badge seul », « copropriétaire »…), dont une par défaut — celle d'une invitation qui n'en
 * désigne aucune. Chacune a son lien d'invitation : un lien ne sait pas qui l'ouvre.
 */
export default {
  name: 'ChargingPolicies',

  components: {AuditTrailButton,  ChargingSettingsTabs },

  mixins: [CommissioningMixin],

  data() {
    return {
      policies: [],
      /** La partie équipement de chaque politique (ce qu'elle désigne, ses badges), par politique. */
      equipment: {},
      equipmentCatalogue: [],
      profiles: [],
      memberTypes: [],
      loading: false,
      error: null,
      withArchived: false,
      creation: { open: false, name: '', copyFrom: null, running: false, error: null },
    }
  },

  computed: {
    organizationId() {
      return this.$route.params.id
    },

    canManage() {
      return (this.currentUser?.permissions || []).includes('ONBOARDING_MODULE_MANAGE')
    },

    copyOptions() {
      return [
        { value: null, text: this.$t('onboarding.charging-policy.list.blank') },
        ...this.policies.filter(p => !p.archived).map(p => ({ value: p.id, text: this.$t('onboarding.charging-policy.list.copy-of', { name: p.name }) })),
      ]
    },
  },

  watch: {
    // Le compte connu après coup (rechargement de la page) : on charge dès que le droit est su.
    canManage(on) {
      if (on) this.load()
    },
  },

  created() {
    if (this.canManage) this.load()
  },

  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        const search = { organizationId: this.organizationId, offset: 0, limit: 200 }
        const [policies, catalogue, profiles, types] = await Promise.all([
          ChargingPolicyRepository.list(this.organizationId, this.withArchived),
          policyCatalogue().catch(() => []),
          EquipmentProfileRepository.list(this.organizationId, true).catch(() => []),
          MemberTypeRepository.search(search).catch(() => ({ items: [] })),
        ])
        // La partie équipement n'est pas dans la liste (autre contexte) : lue politique par politique.
        const equipment = await Promise.all(policies.map(p => CommissioningPolicyRepository.get(this.organizationId, null, p.id).catch(() => null)))
        this.equipmentCatalogue = catalogue
        this.profiles = profiles
        this.memberTypes = types.items || []
        this.equipment = Object.fromEntries(policies.map((p, i) => [p.id, equipment[i]]))
        this.policies = policies
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.loading = false
      }
    },

    /** Un réglage de la partie équipement d'une politique : sa valeur, sinon le défaut du catalogue. */
    equipmentValue(policy, key) {
      const setting = this.equipment[policy.id]?.settings?.[key]
      return setting ? setting.value : this.equipmentCatalogue.find(d => d.key === key)?.default ?? null
    },

    /** Les tiroirs de la fiche, résumés : son adhésion, ses badges, à domicile, au travail, son véhicule, ce qu'elle paie. */
    boxesOf(policy) {
      const settings = policy.settings || {}
      const announced = settings['membership.equipment'] || []
      const designated = this.equipmentValue(policy, PROFILES_KEY) || {}
      const orderable = this.equipmentValue(policy, KINDS_KEY) || []
      const personPays = settings['payment.method'] && settings['payment.method'] !== 'not-asked'
      const kinds = list => list.map(kind => this.kindLabel(kind).toLowerCase()).join(', ')
      const t = (key, params) => this.$t(`onboarding.charging-policy.list.box.${key}`, params)

      // Son adhésion : son type de membre, et si le gestionnaire valide les inscriptions par le lien.
      const memberType = this.memberTypes.find(m => m.id === settings['membership.member-type'])
      const membership = [
        memberType ? this.$t('onboarding.charging-policy.drawer-summary.member-type', { type: memberType.name }) : this.$t('onboarding.charging-policy.drawer-summary.membership-default'),
        // Imposée quand l'adhésion commande du matériel (le serveur la force).
        (announced.length > 0 || settings['membership.link-approval'] !== false) ? this.$t('onboarding.charging-policy.drawer-summary.link-approval') : null,
      ].filter(Boolean).join(' · ')

      // Ses badges : ce qu'elle reçoit dès son arrivée, et si l'organisation paie le réseau public.
      const badges = orderable.includes('badge') && announced.includes('badge')
      const supply = this.equipmentValue(policy, 'badges.supply') || 'professional'
      const badgeText = badges
        ? [this.$t(`onboarding.commissioning.badge-supply.${supply}`), ['professional', 'both'].includes(supply) ? this.$t(`onboarding.commissioning.badge-roaming.${this.equipmentValue(policy, 'badges.roaming') || 'as-stocked'}`).toLowerCase() : null].filter(Boolean).join(' · ')
        : this.$t('onboarding.charging-policy.list.badges-not-announced')

      // À domicile : ce que la politique désigne (des profils), dès son arrivée ou quand le gestionnaire le décide.
      const byDeviceType = this.equipmentValue(policy, PROFILES_BY_DEVICE_TYPE_KEY) || {}
      const homeKinds = HOME_KINDS.filter(kind => designated[kind] || Object.values(byDeviceType[kind] || {}).some(Boolean))
      const atArrival = homeKinds.filter(kind => announced.includes(kind))
      const byManager = homeKinds.filter(kind => !announced.includes(kind))
      const home = [
        atArrival.length ? t('home-arrival', { equipment: kinds(atArrival) }) : null,
        byManager.length ? t('home-manager', { equipment: kinds(byManager) }) : null,
      ].filter(Boolean).join(' · ') || this.$t('onboarding.charging-policy.home.mode.none')

      // Au travail : les profils de site qui ouvrent leurs bornes à un de ses groupes (lu, pas recopié).
      const groups = settings['membership.groups'] || []
      const work = this.profiles
        .filter(p => p.kind === 'charging-point' && !p.archived && ['private', 'shared'].includes(p.usage) && (p.access || []).some(g => groups.includes(g.groupId)))
        .map(p => p.name)

      return [
        { key: 'membership', icon: 'mdi-account-check-outline', title: this.$t('onboarding.charging-policy.drawer.membership'), text: membership },
        { key: 'badges', icon: 'mdi-card-account-details-outline', title: this.$t('onboarding.charging-policy.badges.title'), text: badgeText },
        { key: 'home', icon: 'mdi-home-lightning-bolt-outline', title: this.$t('onboarding.charging-policy.home.title'), text: home },
        { key: 'work', icon: 'mdi-office-building-outline', title: this.$t('onboarding.charging-policy.drawer.work'), text: work.length ? work.join(', ') : this.$t('onboarding.charging-policy.drawer-summary.no-work') },
        {
          key: 'vehicle',
          icon: 'mdi-car-connected',
          title: this.$t('onboarding.charging-policy.drawer.vehicle'),
          // La même règle à trois cas que la fiche : suivi (télématique), suivi par le compteur embarqué seul, ou non suivi.
          text: designated[CONNECTED_VEHICLE]
            ? this.$t('onboarding.charging-policy.drawer-summary.vehicle-followed')
            : (designated['vehicle-meter']
              ? this.$t('onboarding.charging-policy.drawer-summary.vehicle-with-meter')
              : this.$t('onboarding.charging-policy.drawer-summary.vehicle-not-followed')),
        },
        {
          key: 'pays',
          icon: 'mdi-credit-card-outline',
          title: this.$t('onboarding.charging-policy.drawer.pays'),
          text: personPays
            ? this.$t('onboarding.charging-policy.list.person-pays', { billing: this.$t(`onboarding.configuration.value.${settings['payment.billing-mode']}`).toLowerCase() })
            : this.$t('onboarding.charging-policy.list.organization-pays'),
        },
      ]
    },

    linkLabel(policy) {
      if (!policy.link) return this.$t('onboarding.charging-policy.list.link-none')
      return policy.link.active ? this.$t('onboarding.charging-policy.list.link-open', { uses: policy.link.uses }) : this.$t('onboarding.charging-policy.list.link-closed')
    },

    linkColor(policy) {
      return policy.link?.active ? 'green' : 'grey'
    },

    async update(policy, change, success) {
      try {
        await ChargingPolicyRepository.update(this.organizationId, policy.id, change)
        this.notifySuccess(success)
        await this.load()
      } catch (e) {
        this.error = this.messageOf(e)
      }
    },

    /** Archiver ne se défait pas : son lien de partage et ses invitations en cours cessent de marcher. On le dit avant. */
    async archive(policy) {
      const confirmed = await this.$dialog.confirm({
        title: this.$t('onboarding.charging-policy.list.archive-title', { name: policy.name }),
        text: this.$t('onboarding.charging-policy.list.archive-text'),
        persistent: true,
      })
      if (confirmed) await this.update(policy, { archived: true }, 'onboarding.charging-policy.list.archived-ok')
    },

    async remove(policy) {
      const confirmed = await this.$dialog.confirm({
        title: this.$t('onboarding.charging-policy.list.delete-title', { name: policy.name }),
        text: this.$t('onboarding.charging-policy.list.delete-text'),
      })
      if (!confirmed) return
      try {
        await ChargingPolicyRepository.remove(this.organizationId, policy.id)
        this.notifySuccess('onboarding.charging-policy.list.deleted-ok')
        await this.load()
      } catch (e) {
        this.error = this.messageOf(e)
      }
    },

    openCreate(from = null) {
      this.creation = {
        open: true,
        name: from?.id ? this.$t('onboarding.charging-policy.list.copy-name', { name: from.name }) : '',
        copyFrom: from?.id || (this.policies.find(p => p.default)?.id ?? null),
        running: false,
        error: null,
      }
    },

    async create() {
      this.creation.running = true
      this.creation.error = null
      try {
        const { id } = await ChargingPolicyRepository.create(this.organizationId, this.creation.name.trim(), this.creation.copyFrom)
        this.creation.open = false
        this.$router.push({ name: 'organizations.charging-policy', params: { policyId: id } })
      } catch (e) {
        this.creation.error = this.messageOf(e)
      } finally {
        this.creation.running = false
      }
    },

    messageOf(e) {
      const data = e?.response?.data
      const key = data?.messageKey
      if (key && this.$te(key)) return this.$t(key, data.parameters || {})
      return data?.message || this.$t('error-occurred')
    },
  },
}
</script>

<style scoped>
.policy-card__name {
  text-decoration: none;
  color: inherit;
}
.policy-card__name:hover {
  text-decoration: underline;
}
.policy-card--archived {
  opacity: 0.6;
}
.policy-card__box {
  background: #f6f5f3;
  border-radius: 8px;
  padding: 10px 12px;
  height: 100%;
}
.policy-card__box-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.55);
  margin-bottom: 2px;
}
</style>
